import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from '../../firebase';
import {
  RecaptchaVerifier,
  PhoneAuthProvider,
  linkWithCredential,
  signInWithPhoneNumber,
  updatePassword,
} from "firebase/auth";
import {
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { userService } from "../../services/firestoreService";
import { updateUserEmailByUid } from "../../services/adminService";
import {
  createOtpRequestId,
  generateOtpCode,
  sendOtpEmail,
  sendOtpSms,
  sendPasswordChangeNotification,
  OTP_EXPIRATION_MS,
} from "../../services/otpService";
import { hashPassword } from "../../services/passwordUtils";
import './otpmodalscss/UserChangeOtpModal.css';

const UserChangeOtpModal = ({ 
  isOpen, 
  onClose, 
  changeType, // 'email', 'contact', 'password'
  changeData,
  onSuccess,
  userEmail = '',
  userPhone = '',
  userFirstName = ''
}) => {
  const OTP_LENGTH = 6;
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [error, setError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [isExpired, setIsExpired] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [profileChangeStep, setProfileChangeStep] = useState("oldEmail");
  const [combinedChangeStep, setCombinedChangeStep] = useState("verifyIdentity");
  const [oldOtpChallenge, setOldOtpChallenge] = useState(null);
  const [newOtpChallenge, setNewOtpChallenge] = useState(null);
  const [smsOtpChallenge, setSmsOtpChallenge] = useState(null);
  const [verificationMethod, setVerificationMethod] = useState("email");
  const [hasSmsBeenSent, setHasSmsBeenSent] = useState(false);
  const [hasEmailBeenSent, setHasEmailBeenSent] = useState(false);
  const [timerActive, setTimerActive] = useState(true);
  const [oldSmsOtpChallenge, setOldSmsOtpChallenge] = useState(null);
  const [newSmsOtpChallenge, setNewSmsOtpChallenge] = useState(null);
  const [passwordVerificationMethod, setPasswordVerificationMethod] = useState("email");
  const [passwordSmsChallenge, setPasswordSmsChallenge] = useState(null);
  const [passwordHasSmsBeenSent, setPasswordHasSmsBeenSent] = useState(false);
  const [passwordHasEmailBeenSent, setPasswordHasEmailBeenSent] = useState(false);
  const [passwordTimerActive, setPasswordTimerActive] = useState(true);
  const [showEmailSuccessModal, setShowEmailSuccessModal] = useState(false);
  const [contactChangeStep, setContactChangeStep] = useState("oldContact");
  const [oldContactOtpChallenge, setOldContactOtpChallenge] = useState(null);
  const [newContactOtpChallenge, setNewContactOtpChallenge] = useState(null);
  const [oldContactSmsOtpChallenge, setOldContactSmsOtpChallenge] = useState(null);
  const [newContactSmsOtpChallenge, setNewContactSmsOtpChallenge] = useState(null);
  
  const inputsRef = useRef([]);
  const recaptchaVerifierRef = useRef(null);
  const otpSentRef = useRef(false);

  // Focus first input when error occurs and OTP is cleared
  useEffect(() => {
    if (error && otp.every(digit => digit === '') && !isVerifying) {
      const timer = setTimeout(() => {
        inputsRef.current[0]?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [error, otp, isVerifying]);

  // Compute profile change variables
  const oldEmail = changeData?.currentEmail || changeData?.originalData?.email || "";
  const newEmail = changeData?.updatedData?.email || "";
  const oldContact = changeData?.currentContact || changeData?.originalData?.contact || "";
  const newContact = changeData?.updatedData?.contact || "";
  const normalizedOldContact = String(oldContact || "").replace(/\D/g, "").slice(-10);
  const normalizedNewContact = String(newContact || "").replace(/\D/g, "").slice(-10);
  const hasEmailChange = Boolean(
    oldEmail &&
    newEmail &&
    oldEmail.trim().toLowerCase() !== newEmail.trim().toLowerCase()
  );
  const hasContactChange = Boolean(
    normalizedNewContact &&
    normalizedOldContact &&
    normalizedNewContact !== normalizedOldContact
  );
  const hasProfileChange = hasEmailChange || hasContactChange;

  const normalizePhone = (phone) => {
    if (!phone) return null;
    const cleaned = String(phone).replace(/\D/g, "");
    if (cleaned.length === 10 && cleaned.startsWith("9")) {
      return `+63${cleaned}`;
    } else if (cleaned.length === 11 && cleaned.startsWith("0")) {
      return `+63${cleaned.slice(1)}`;
    } else if (cleaned.length === 12 && cleaned.startsWith("63")) {
      return `+${cleaned}`;
    } else if (phone.startsWith("+63")) {
      return phone;
    }
    return null;
  };

  const oldPhone = changeData?.originalData?.phone || 
                  (changeData?.originalData?.phoneLast10 ? `+63${changeData.originalData.phoneLast10}` : null) || 
                  changeData?.originalData?.contact;
  const normalizedOldPhone = normalizePhone(oldPhone) || normalizePhone(userPhone);
  const normalizedPhoneForPassword = normalizePhone(userPhone || changeData?.phone);

  // Auto-focus first input when modal opens
  useEffect(() => {
    if (isOpen) {
      inputsRef.current[0]?.focus();
    }
  }, [isOpen]);

  // Countdown timer
  useEffect(() => {
    if (!isOpen || !timerActive || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setIsExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, timeLeft, timerActive]);

  // Initialize OTP flow when modal opens
  useEffect(() => {
    if (!isOpen || otpSentRef.current) return;

    const timer = setTimeout(async () => {
      if (otpSentRef.current) return;
      otpSentRef.current = true;

      if (changeType === 'password') {
        await initializePasswordChangeFlow();
      } else if (changeType === 'contact' || (hasContactChange && !hasEmailChange)) {
        // Set SMS as default for contact-only changes
        setVerificationMethod("sms");
        setProfileChangeStep("oldContact");
        await initializeContactChangeFlow();
      } else if (hasEmailChange && hasContactChange) {
        // Combined email and contact change - 3-step flow
        setCombinedChangeStep("verifyIdentity");
        setVerificationMethod("email");
        await initializeProfileChangeFlow();
      } else if (hasProfileChange) {
        await initializeProfileChangeFlow();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [isOpen, changeType, hasProfileChange, hasContactChange, hasEmailChange]);

  const initializePasswordChangeFlow = async () => {
    setIsSendingOtp(true);

    try {
      const email = userEmail || changeData?.email;
      if (!email) {
        throw new Error("Unable to send OTP. Email is missing.");
      }

      const challenge = await sendOtpToEmail(email, "password-change", setOldOtpChallenge);
      setPasswordHasEmailBeenSent(true);
    } catch (err) {
      setError(err.message || "Unable to send verification code.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  const initializeProfileChangeFlow = async () => {
    setIsSendingOtp(true);

    try {
      if (!oldEmail || !oldEmail.includes('@')) {
        throw new Error("Unable to send OTP. Email is missing.");
      }

      const challenge = await sendOtpToEmail(oldEmail, "profile-change-old-email", setOldOtpChallenge);
      setHasEmailBeenSent(true);
      setTimerActive(true);
    } catch (err) {
      setError(err.message || "Unable to send verification code.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  const initializeContactChangeFlow = async () => {
    // Don't auto-send for contact changes - user must click Send SMS
    setTimerActive(false);
  };

  const sendOtpToEmail = async (email, purpose, saveChallenge) => {
    if (!email) {
      throw new Error("Unable to send OTP. Email is missing.");
    }

    const otpCode = generateOtpCode();
    const requestId = createOtpRequestId(purpose);
    const expiresAt = Date.now() + OTP_EXPIRATION_MS;

    await sendOtpEmail({
      email,
      otp: otpCode,
      purpose,
      requestId,
      expiresAt,
    });

    const challenge = { email, otp: otpCode, requestId, expiresAt };
    saveChallenge(challenge);
    setOtp(Array(OTP_LENGTH).fill(""));
    setIsExpired(false);
    setTimeLeft(60);
    setError("");
    return challenge;
  };

  const getRecaptchaVerifier = () => {
    if (recaptchaVerifierRef.current) {
      try {
        recaptchaVerifierRef.current.clear();
      } catch (e) {
        console.warn("Error clearing existing reCAPTCHA verifier:", e);
      }
    }
    if (typeof window === "undefined") return null;

    const container = document.getElementById("recaptcha-container-modal");
    if (!container) {
      console.warn("reCAPTCHA container not found in DOM");
      return null;
    }

    try {
      const verifier = new RecaptchaVerifier(
        "recaptcha-container-modal",
        {
          size: "invisible",
          callback: (response) => {
            console.log("reCAPTCHA solved successfully");
          },
          "expired-callback": () => {
            console.log("reCAPTCHA expired");
          }
        },
        auth
      );

      verifier.render().catch((err) => {
        console.warn("reCAPTCHA render failed:", err);
      });

      recaptchaVerifierRef.current = verifier;
      return verifier;
    } catch (error) {
      console.warn("Failed to create reCAPTCHA verifier:", error);
      return null;
    }
  };

  const sendOtpToSms = async (phone, purpose, saveChallenge) => {
    if (!phone) {
      throw new Error("Unable to send SMS OTP. Phone number is missing.");
    }

    const otpCode = generateOtpCode();
    const requestId = createOtpRequestId(purpose);
    const expiresAt = Date.now() + OTP_EXPIRATION_MS;

    await sendOtpSms({
      phone,
      otp: otpCode,
      purpose,
      requestId,
      expiresAt,
    });

    const challenge = { phone, otp: otpCode, requestId, expiresAt };
    saveChallenge(challenge);
    setOtp(Array(OTP_LENGTH).fill(""));
    setIsExpired(false);
    setTimeLeft(60);
    setTimerActive(true);
    setError("");
    return challenge;
  };

  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (error) setError("");

    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasteData = e.clipboardData.getData("text").trim().slice(0, OTP_LENGTH);
    if (/^\d+$/.test(pasteData)) {
      const pasteArray = pasteData.split("");
      const newOtp = Array(OTP_LENGTH).fill("");
      pasteArray.forEach((char, i) => {
        if (i < OTP_LENGTH) newOtp[i] = char;
      });

      setOtp(newOtp);
      setError("");
      const focusIndex = Math.min(pasteArray.length, OTP_LENGTH - 1);
      inputsRef.current[focusIndex]?.focus();
    }
    e.preventDefault();
  };

  const handleVerify = async () => {
    if (!otp.every(digit => digit !== "")) {
      setError("Please enter the full 6-digit code.");
      setOtp(Array(OTP_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
      return;
    }

    if (isExpired) {
      setError("OTP expired. Please request a new code.");
      setOtp(Array(OTP_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
      return;
    }

    setIsVerifying(true);
    try {
      if (changeType === 'password') {
        await handlePasswordChangeVerification();
      } else if (hasProfileChange) {
        await handleProfileChangeVerification();
      }
    } catch (err) {
      setError(err.message || 'Invalid OTP');
      setOtp(Array(OTP_LENGTH).fill(""));
    } finally {
      setIsVerifying(false);
    }
  };

  const handlePasswordChangeVerification = async () => {
    const otpCode = otp.join("");
    
    // Get the appropriate challenge based on verification method
    const challenge = passwordVerificationMethod === 'sms' ? passwordSmsChallenge : oldOtpChallenge;

    if (!challenge || Date.now() > challenge.expiresAt) {
      throw new Error("OTP expired or invalid. Please request a new code.");
    }

    if (otpCode !== challenge.otp) {
      throw new Error("Invalid OTP. Please try again.");
    }

    // Update password
    const newPassword = changeData?.newPassword;
    if (!newPassword) {
      throw new Error("New password is missing.");
    }

    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not authenticated.");
    }

    await updatePassword(user, newPassword);

    // Send notification
    await sendPasswordChangeNotification({
      email: userEmail,
      firstName: userFirstName,
    });

    // Update Firestore
    const uid = user.uid;
    const hashedPassword = await hashPassword(newPassword);
    await updateDoc(doc(db, "users", uid), {
      password: hashedPassword,
      lastPasswordChange: serverTimestamp(),
    });

    onSuccess({ type: 'password', requireReauth: true });
    handleClose();
  };

  const handleProfileChangeVerification = async () => {
    const otpCode = otp.join("");
    
    // Handle 3-step combined email and contact change
    if (hasEmailChange && hasContactChange) {
      // Step 1: Verify identity (old email/phone)
      if (combinedChangeStep === "verifyIdentity") {
        const challenge = verificationMethod === 'sms' ? oldSmsOtpChallenge : oldOtpChallenge;

        if (!challenge || Date.now() > challenge.expiresAt) {
          throw new Error("OTP expired or invalid. Please request a new code.");
        }

        if (otpCode !== challenge.otp) {
          throw new Error("Invalid OTP. Please try again.");
        }

        // Move to step 2 (verify new email)
        setCombinedChangeStep("verifyNewEmail");
        setOtp(Array(OTP_LENGTH).fill(""));
        setTimeLeft(60);
        setIsExpired(false);
        setError("");
        setVerificationMethod("email");
        
        // Send OTP to new email
        setIsSendingOtp(true);
        
        try {
          await sendOtpToEmail(newEmail, "profile-change-new-email", setNewOtpChallenge);
        } catch (err) {
          setError(err.message || "Unable to send verification code to new email.");
          setCombinedChangeStep("verifyIdentity");
        } finally {
          setIsSendingOtp(false);
        }
        return;
      }
      
      // Step 2: Verify new email
      if (combinedChangeStep === "verifyNewEmail") {
        const challenge = newOtpChallenge;

        if (!challenge || Date.now() > challenge.expiresAt) {
          throw new Error("OTP expired or invalid. Please request a new code.");
        }

        if (otpCode !== challenge.otp) {
          throw new Error("Invalid OTP. Please try again.");
        }

        // Move to step 3 (verify new phone)
        setCombinedChangeStep("verifyNewPhone");
        setOtp(Array(OTP_LENGTH).fill(""));
        setTimeLeft(60);
        setIsExpired(false);
        setError("");
        setVerificationMethod("sms");
        setHasSmsBeenSent(false); // Reset so Send SMS button appears
        setTimerActive(false); // Don't start timer until Send SMS is clicked
        return;
      }
      
      // Step 3: Verify new phone and complete both changes
      if (combinedChangeStep === "verifyNewPhone") {
        const challenge = newContactSmsOtpChallenge;

        if (!challenge || Date.now() > challenge.expiresAt) {
          throw new Error("OTP expired or invalid. Please request a new code.");
        }

        if (otpCode !== challenge.otp) {
          throw new Error("Invalid OTP. Please try again.");
        }

        // Process both email and contact changes
        const uid = changeData?.uid;
        if (!uid) {
          throw new Error("User ID is missing.");
        }

        const updateData = {
          email: newEmail,
          emailLower: newEmail.toLowerCase(),
          "contactDetails.email": newEmail,
          contact: newContact,
          phone: `+63${normalizedNewContact}`,
          phoneLast10: normalizedNewContact,
          "contactDetails.contact": newContact,
          updatedAt: serverTimestamp(),
        };

        const idToken = await auth.currentUser.getIdToken();
        await updateUserEmailByUid({ uid, email: newEmail, idToken });

        await updateDoc(doc(db, "users", uid), updateData);

        setShowEmailSuccessModal(true);
        return;
      }
    }
    
    // Handle contact change verification
    if (profileChangeStep === "oldContact") {
      const challenge = verificationMethod === 'sms' ? oldContactSmsOtpChallenge : oldContactOtpChallenge;

      if (!challenge || Date.now() > challenge.expiresAt) {
        throw new Error("OTP expired or invalid. Please request a new code.");
      }

      if (otpCode !== challenge.otp) {
        throw new Error("Invalid OTP. Please try again.");
      }

      // Move to step 2 (verify new contact)
      setProfileChangeStep("newContact");
      setOtp(Array(OTP_LENGTH).fill(""));
      setTimeLeft(60);
      setIsExpired(false);
      setError("");
      setVerificationMethod("sms"); // SMS for new contact verification
      setHasSmsBeenSent(false); // Reset so Send SMS button appears
      setTimerActive(false); // Don't start timer until Send SMS is clicked
      return;
    }
    
    // Step 2: Verify new contact and complete change
    if (profileChangeStep === "newContact") {
      const challenge = newContactSmsOtpChallenge;

      if (!challenge || Date.now() > challenge.expiresAt) {
        throw new Error("OTP expired or invalid. Please request a new code.");
      }

      if (otpCode !== challenge.otp) {
        throw new Error("Invalid OTP. Please try again.");
      }

      // Process contact change
      const uid = changeData?.uid;
      if (!uid) {
        throw new Error("User ID is missing.");
      }

      await updateDoc(doc(db, "users", uid), {
        contact: newContact,
        phone: `+63${normalizedNewContact}`,
        phoneLast10: normalizedNewContact,
        "contactDetails.contact": newContact,
        updatedAt: serverTimestamp(),
      });

      onSuccess({ type: 'contact' });
      handleClose();
      return;
    }
    
    // Step 1: Verify old email/phone
    if (profileChangeStep === "oldEmail") {
      // Get the appropriate challenge based on verification method
      const challenge = verificationMethod === 'sms' ? oldSmsOtpChallenge : oldOtpChallenge;

      if (!challenge || Date.now() > challenge.expiresAt) {
        throw new Error("OTP expired or invalid. Please request a new code.");
      }

      if (otpCode !== challenge.otp) {
        throw new Error("Invalid OTP. Please try again.");
      }

      // If email change, move to step 2 (verify new email)
      if (hasEmailChange) {
        setProfileChangeStep("newEmail");
        setOtp(Array(OTP_LENGTH).fill(""));
        setTimeLeft(60);
        setIsExpired(false);
        setError("");
        setVerificationMethod("email"); // Reset to email for new email verification
        
        // Send OTP to new email
        setIsSendingOtp(true);
        
        try {
          await sendOtpToEmail(newEmail, "profile-change-new-email", setNewOtpChallenge);
        } catch (err) {
          setError(err.message || "Unable to send verification code to new email.");
          setProfileChangeStep("oldEmail"); // Go back if sending fails
        } finally {
          setIsSendingOtp(false);
        }
        return;
      }
      
      // If only contact change, proceed with update
      if (hasContactChange) {
        const uid = changeData?.uid;
        if (!uid) {
          throw new Error("User ID is missing.");
        }

        await updateDoc(doc(db, "users", uid), {
          contact: newContact,
          phone: `+63${normalizedNewContact}`,
          updatedAt: serverTimestamp(),
        });

        onSuccess({ type: 'contact' });
        handleClose();
        return;
      }
    }
    
    // Step 2: Verify new email and complete change
    if (profileChangeStep === "newEmail") {
      const challenge = newOtpChallenge;

      if (!challenge || Date.now() > challenge.expiresAt) {
        throw new Error("OTP expired or invalid. Please request a new code.");
      }

      if (otpCode !== challenge.otp) {
        throw new Error("Invalid OTP. Please try again.");
      }

      // Process email change
      const uid = changeData?.uid;
      if (!uid) {
        throw new Error("User ID is missing.");
      }

      const updateData = {
        email: newEmail,
        emailLower: newEmail.toLowerCase(),
        "contactDetails.email": newEmail,
      };

      const idToken = await auth.currentUser.getIdToken();
      await updateUserEmailByUid({ uid, email: newEmail, idToken });

      if (hasContactChange) {
        updateData.contact = newContact;
        updateData.phone = `+63${normalizedNewContact}`;
      }

      await updateDoc(doc(db, "users", uid), {
        ...updateData,
        updatedAt: serverTimestamp(),
      });

      setShowEmailSuccessModal(true);
    }
  };

  const handleResendOtp = async () => {
    if (changeType === 'password') {
      await initializePasswordChangeFlow();
    } else if (hasEmailChange && hasContactChange) {
      // Combined email and contact change
      if (combinedChangeStep === "verifyIdentity") {
        await initializeProfileChangeFlow();
      } else if (combinedChangeStep === "verifyNewEmail") {
        setIsSendingOtp(true);
        
        try {
          await sendOtpToEmail(newEmail, "profile-change-new-email", setNewOtpChallenge);
        } catch (err) {
          setError(err.message || "Unable to send verification code to new email.");
        } finally {
          setIsSendingOtp(false);
        }
      } else if (combinedChangeStep === "verifyNewPhone") {
        setIsSendingOtp(true);
        
        try {
          await sendOtpToSms(`+63${normalizedNewContact}`, "contact-change-new-sms", setNewContactSmsOtpChallenge);
        } catch (err) {
          setError(err.message || "Unable to send verification code to new phone.");
        } finally {
          setIsSendingOtp(false);
        }
      }
    } else if (hasContactChange) {
      if (profileChangeStep === "oldContact") {
        await initializeContactChangeFlow();
      } else if (profileChangeStep === "newContact") {
        setIsSendingOtp(true);
        
        try {
          await sendOtpToSms(`+63${normalizedNewContact}`, "contact-change-new-sms", setNewContactSmsOtpChallenge);
        } catch (err) {
          setError(err.message || "Unable to send verification code to new phone.");
        } finally {
          setIsSendingOtp(false);
        }
      }
    } else if (hasProfileChange) {
      if (profileChangeStep === "oldEmail") {
        await initializeProfileChangeFlow();
      } else if (profileChangeStep === "newEmail") {
        setIsSendingOtp(true);
        
        try {
          await sendOtpToEmail(newEmail, "profile-change-new-email", setNewOtpChallenge);
        } catch (err) {
          setError(err.message || "Unable to send verification code to new email.");
        } finally {
          setIsSendingOtp(false);
        }
      }
    }
  };

  const handleTryAnotherWay = async () => {
    const newMethod = verificationMethod === 'email' ? 'sms' : 'email';
    console.log('🔄 handleTryAnotherWay: Switching from', verificationMethod, 'to', newMethod);
    setVerificationMethod(newMethod);
    setHasSmsBeenSent(false);
    setHasEmailBeenSent(false);

    setOtp(Array(OTP_LENGTH).fill(""));
    setError("");
    setTimeLeft(60);
    setIsExpired(false);
    
    // Don't auto-send for contact changes - user must click send button
    if (hasContactChange) {
      setTimerActive(false);
    } else if (newMethod === 'email' && hasEmailChange) {
      // Email change switching back to email — show Send Email button instead
      setTimerActive(false);
    } else if (newMethod === 'email') {
      setTimerActive(true);
      // Auto-send OTP when switching to email (non-email change types)
      setIsSendingOtp(true);
      
      try {
        if (profileChangeStep === "oldEmail") {
          await sendOtpToEmail(oldEmail, "profile-change-old-email", setOldOtpChallenge);
        } else if (profileChangeStep === "newEmail") {
          await sendOtpToEmail(newEmail, "profile-change-new-email", setNewOtpChallenge);
        } else if (profileChangeStep === "oldContact") {
          await sendOtpToEmail(userEmail, "contact-change-old-email", setOldContactOtpChallenge);
        }
      } catch (err) {
        setError(err.message || "Unable to send verification code.");
      } finally {
        setIsSendingOtp(false);
      }
    } else {
      // Don't auto-send when switching to SMS
      setTimerActive(false);
    }
  };

  const handlePasswordTryAnotherWay = async () => {
    const newMethod = passwordVerificationMethod === 'email' ? 'sms' : 'email';
    console.log('🔄 handlePasswordTryAnotherWay: Switching from', passwordVerificationMethod, 'to', newMethod);
    setPasswordVerificationMethod(newMethod);
    setPasswordHasSmsBeenSent(false);

    setOtp(Array(OTP_LENGTH).fill(""));
    setError("");
    setTimeLeft(60);
    setIsExpired(false);
    
    if (newMethod === 'email') {
      // Don't auto-send — show Send Email button instead
      setPasswordHasEmailBeenSent(false);
      setTimerActive(false);
    } else {
      // Don't auto-send when switching to SMS - wait for user to click Send SMS
      setTimerActive(false);
    }
  };

  const handlePasswordSendSms = async () => {
    // Prevent double-sending
    if (passwordHasSmsBeenSent) return;
    
    try {
      const phone = userPhone;
      if (!phone) {
        throw new Error("Phone number not available");
      }
      
      await sendOtpToSms(phone, "password-change", setPasswordSmsChallenge);
      setPasswordHasSmsBeenSent(true);
      setTimerActive(true);
    } catch (err) {
      setError(err.message || "Unable to send verification code to phone.");
    }
  };

  const handleSendSms = async () => {
    // Prevent double-sending
    if (hasSmsBeenSent) return;
    
    try {
      // Use userPhone directly like TwoFactorOtpModal does
      const phone = userPhone;
      if (!phone) {
        throw new Error("Phone number not available");
      }
      
      if (profileChangeStep === "oldEmail") {
        await sendOtpToSms(phone, "profile-change-old-sms", setOldSmsOtpChallenge);
      } else if (profileChangeStep === "newEmail") {
        await sendOtpToSms(phone, "profile-change-new-sms", setNewSmsOtpChallenge);
      } else if (profileChangeStep === "oldContact") {
        await sendOtpToSms(phone, "contact-change-old-sms", setOldContactSmsOtpChallenge);
      } else if (profileChangeStep === "newContact") {
        await sendOtpToSms(`+63${normalizedNewContact}`, "contact-change-new-sms", setNewContactSmsOtpChallenge);
      }
      setHasSmsBeenSent(true);
      setTimerActive(true);
    } catch (err) {
      setError(err.message || "Unable to send verification code to phone.");
    }
  };

  const handleSendEmail = async () => {
    // Prevent double-sending
    if (hasEmailBeenSent) return;
    
    try {
      const email = userEmail;
      if (!email) {
        throw new Error("Email not available");
      }
      
      if (profileChangeStep === "oldContact") {
        await sendOtpToEmail(email, "contact-change-old-email", setOldContactOtpChallenge);
      } else if (profileChangeStep === "oldEmail") {
        await sendOtpToEmail(oldEmail || email, "profile-change-old-email", setOldOtpChallenge);
      }
      setHasEmailBeenSent(true);
      setTimerActive(true);
    } catch (err) {
      setError(err.message || "Unable to send verification code to email.");
    }
  };

  const handleClose = () => {
    setOtp(Array(OTP_LENGTH).fill(""));
    setError('');
    setTimeLeft(60);
    setIsExpired(false);
    setProfileChangeStep("oldEmail");
    setContactChangeStep("oldContact");
    setCombinedChangeStep("verifyIdentity");
    setOldOtpChallenge(null);
    setNewOtpChallenge(null);
    setSmsOtpChallenge(null);
    setOldContactOtpChallenge(null);
    setNewContactOtpChallenge(null);
    setOldContactSmsOtpChallenge(null);
    setNewContactSmsOtpChallenge(null);
    setVerificationMethod("email");
    setHasSmsBeenSent(false);
    setTimerActive(true);
    setShowEmailSuccessModal(false);
    otpSentRef.current = false;
    onClose();
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getDisplayTarget = () => {
    if (changeType === 'password') {
      if (passwordVerificationMethod === 'sms') {
        return normalizedPhoneForPassword || userPhone || 'your phone';
      }
      return userEmail || 'your email';
    }
    
    // For combined email and contact changes
    if (hasEmailChange && hasContactChange) {
      if (combinedChangeStep === "verifyIdentity") {
        return verificationMethod === 'sms' ? userPhone : (oldEmail || userEmail);
      }
      if (combinedChangeStep === "verifyNewEmail") {
        return newEmail;
      }
      if (combinedChangeStep === "verifyNewPhone") {
        return `+63${normalizedNewContact}`;
      }
    }
    
    // For contact changes
    if (hasContactChange) {
      if (profileChangeStep === "oldContact") {
        return verificationMethod === 'sms' ? userPhone : userEmail;
      }
      if (profileChangeStep === "newContact") {
        return `+63${normalizedNewContact}`;
      }
    }
    
    // For profile changes
    if (verificationMethod === 'sms') {
      return normalizedOldPhone || userPhone || 'your phone';
    }
    
    if (profileChangeStep === "newEmail" && hasEmailChange) {
      return newEmail;
    }
    if (profileChangeStep === "oldEmail" && hasEmailChange) {
      return oldEmail || userEmail;
    }
    return oldEmail || userEmail;
  };

  const getTitle = () => {
    if (changeType === 'password') return 'Verify Password Change';
    if (hasEmailChange && hasContactChange) {
      if (combinedChangeStep === "verifyIdentity") return "Verify It's You";
      if (combinedChangeStep === "verifyNewEmail") return 'Verify Your New Email';
      if (combinedChangeStep === "verifyNewPhone") return 'Verify Your New Phone';
    }
    if (hasEmailChange && profileChangeStep === "oldEmail") return "Verify It's You";
    if (hasEmailChange && profileChangeStep === "newEmail") return 'Verify Your New Email';
    if (hasContactChange && profileChangeStep === "oldContact") return "Verify It's You";
    if (hasContactChange && profileChangeStep === "newContact") return 'Verify Your New Phone';
    if (hasContactChange) return 'Verify Contact Change';
    return 'Verify Profile Change';
  };

  const getSubtitle = () => {
    if (changeType === 'password') {
      return 'Enter 6-digit code we sent to: ';
    }
    if (hasEmailChange && hasContactChange) {
      if (combinedChangeStep === "verifyIdentity") {
        return 'Enter 6-digit code we sent to: ';
      }
      if (combinedChangeStep === "verifyNewEmail") {
        return 'Enter 6-digit code we sent to your new email: ';
      }
      if (combinedChangeStep === "verifyNewPhone") {
        return 'Enter 6-digit code we sent to your new phone: ';
      }
    }
    if (hasEmailChange && profileChangeStep === "oldEmail") {
      return 'Enter 6-digit code we sent to your current email: ';
    }
    if (hasEmailChange && profileChangeStep === "newEmail") {
      return 'Enter 6-digit code we sent to your new email: ';
    }
    if (hasContactChange && profileChangeStep === "oldContact") {
      return 'Enter 6-digit code we sent to your current phone: ';
    }
    if (hasContactChange && profileChangeStep === "newContact") {
      return 'Enter 6-digit code we sent to your new phone: ';
    }
    return 'Enter 6-digit code we sent to: ';
  };

  const getStepIndicator = () => {
    if (hasEmailChange && hasContactChange) {
      if (combinedChangeStep === "verifyIdentity") {
        return 'Step 1 of 3';
      }
      if (combinedChangeStep === "verifyNewEmail") {
        return 'Step 2 of 3';
      }
      if (combinedChangeStep === "verifyNewPhone") {
        return 'Step 3 of 3';
      }
    }
    if (hasEmailChange && profileChangeStep === "oldEmail") {
      return 'Step 1 of 2';
    }
    if (hasEmailChange && profileChangeStep === "newEmail") {
      return 'Step 2 of 2';
    }
    if (hasContactChange && profileChangeStep === "oldContact") {
      return 'Step 1 of 2';
    }
    if (hasContactChange && profileChangeStep === "newContact") {
      return 'Step 2 of 2';
    }
    return null;
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="userchange-otp-modal-overlay">
        <div id="recaptcha-container-modal" style={{ display: 'none' }}></div>
        <div className="userchange-otp-modal-card">
          <h1 className="userchange-otp-title">{getTitle()}</h1>
          {getStepIndicator() && (
            <p className="userchange-otp-step-indicator">{getStepIndicator()}</p>
          )}
          <p className="userchange-otp-subtitle">
            {getSubtitle()}<strong>{getDisplayTarget()}</strong>
          </p>

          <div className="userchange-otp-inputs">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputsRef.current[index] = el)}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={handlePaste}
                className={`userchange-otp-input ${error && !digit ? "error" : ""}`}
                disabled={isVerifying}
                autoFocus={index === 0}
              />
            ))}
          </div>

          {error && <div className="userchange-otp-error-text">{error}</div>}

          {isExpired && (
            <div className="userchange-otp-error-text">Code has expired. Please request a new one.</div>
          )}

          {verificationMethod === 'sms' && !hasSmsBeenSent && (
            <span 
              className="userchange-otp-back-link" 
              onClick={handleSendSms}
              style={{ 
                textDecoration: "underline", 
                marginBottom: "8px", 
                display: "block",
                cursor: "pointer",
                color: "#1976d2"
              }}
            >
              Send SMS
            </span>
          )}

          {changeType === 'password' && passwordVerificationMethod === 'sms' && !passwordHasSmsBeenSent && (
            <span 
              className="userchange-otp-back-link" 
              onClick={handlePasswordSendSms}
              style={{ 
                textDecoration: "underline", 
                marginBottom: "8px", 
                display: "block",
                cursor: "pointer",
                color: "#1976d2"
              }}
            >
              Send SMS
            </span>
          )}

          {verificationMethod === 'email' && !hasEmailBeenSent && (hasContactChange || hasEmailChange) && (
            <span 
              className="userchange-otp-back-link" 
              onClick={handleSendEmail}
              style={{ 
                textDecoration: "underline", 
                marginBottom: "8px", 
                display: "block",
                cursor: "pointer",
                color: "#1976d2"
              }}
            >
              Send Email
            </span>
          )}

          {changeType === 'password' && passwordVerificationMethod === 'email' && !passwordHasEmailBeenSent && (
            <span
              className="userchange-otp-back-link"
              onClick={async () => {
                setIsSendingOtp(true);
                try {
                  const email = userEmail || changeData?.email;
                  if (!email) throw new Error("Email not available");
                  await sendOtpToEmail(email, "password-change", setOldOtpChallenge);
                  setPasswordHasEmailBeenSent(true);
                  setTimerActive(true);
                } catch (err) {
                  setError(err.message || "Unable to send verification code.");
                } finally {
                  setIsSendingOtp(false);
                }
              }}
              style={{
                textDecoration: "underline",
                marginBottom: "8px",
                display: "block",
                cursor: isSendingOtp ? "default" : "pointer",
                color: "#1976d2",
                opacity: isSendingOtp ? 0.5 : 1,
                pointerEvents: isSendingOtp ? "none" : "auto",
              }}
            >
              {isSendingOtp ? "Sending..." : "Send Email"}
            </span>
          )}

          {!(changeType === 'password' && passwordVerificationMethod === 'email' && !passwordHasEmailBeenSent) && (
          <p className="userchange-otp-timer-section">
            Code expires in <span className={`userchange-otp-timer ${isExpired ? "expired" : ""}`}>{formatTime(timeLeft)}</span>
          </p>
          )}

          {!(changeType === 'password' && passwordVerificationMethod === 'email' && !passwordHasEmailBeenSent) && (
          <p className="userchange-otp-resend-section">
            Didn't receive a code?{" "}
            <span 
              className={`userchange-otp-resend-link ${isSendingOtp ? "disabled" : ""}`} 
              onClick={handleResendOtp}
              style={{ pointerEvents: isSendingOtp ? "none" : "auto", opacity: isSendingOtp ? 0.5 : 1 }}
            >
              {isSendingOtp ? "Sending..." : "Resend"}
            </span>
          </p>
          )}

          {(changeType !== 'password' && 
            ((hasEmailChange && hasContactChange && combinedChangeStep === "verifyIdentity") ||
             (!hasEmailChange || !hasContactChange) && (profileChangeStep !== "newEmail" && profileChangeStep !== "newContact")) &&
            ((verificationMethod === 'email' && (normalizedOldPhone || userPhone)) || 
            (verificationMethod === 'sms' && userEmail))) && (
            <span 
              className="userchange-otp-back-link" 
              onClick={handleTryAnotherWay}
              style={{ 
                textDecoration: "underline", 
                marginBottom: "8px", 
                display: "block",
                cursor: "pointer",
                color: "#1976d2"
              }}
            >
              Try another way
            </span>
          )}

          {changeType === 'password' && 
            ((passwordVerificationMethod === 'email' && (normalizedPhoneForPassword || userPhone)) || 
            (passwordVerificationMethod === 'sms' && userEmail)) && (
            <span 
              className="userchange-otp-back-link" 
              onClick={handlePasswordTryAnotherWay}
              style={{ 
                textDecoration: "underline", 
                marginBottom: "8px", 
                display: "block",
                cursor: "pointer",
                color: "#1976d2"
              }}
            >
              Try another way
            </span>
          )}

          <button 
            className="userchange-otp-verify-btn" 
            onClick={handleVerify} 
            disabled={isVerifying || !otp.every(d => d !== "") || isExpired}
          >
            {isVerifying ? "Verifying..." : "Verify"}
          </button>

          <button 
            className="userchange-otp-cancel-btn" 
            onClick={handleClose}
            disabled={isVerifying}
          >
            Cancel
          </button>
        </div>
      </div>

      {showEmailSuccessModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10001,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "32px",
              maxWidth: "400px",
              width: "90%",
              textAlign: "center",
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
            }}
          >
            <div
              style={{
                width: "60px",
                height: "60px",
                backgroundColor: "#36C667",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <svg
                width="30"
                height="30"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            
            <h2 style={{ margin: "0 0 16px 0", color: "#333", fontSize: "24px" }}>
              Email Update Successful!
            </h2>
            
            <p style={{ margin: "0 0 24px 0", color: "#666", fontSize: "16px", lineHeight: "1.5" }}>
              Your email has been successfully updated. You need to log in again with your new email address.
            </p>
            
            <button
              onClick={() => {
                setShowEmailSuccessModal(false);
                onSuccess({ type: 'email', requireReauth: true });
                handleClose();
              }}
              style={{
                backgroundColor: "#003d82",
                color: "white",
                border: "none",
                padding: "12px 32px",
                borderRadius: "8px",
                fontSize: "16px",
                cursor: "pointer",
                fontWeight: "500",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#002d62")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "#003d82")}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default UserChangeOtpModal;
